// Import types used in the interface or re-exported
import { PMT2SpreadsheetConfig, PMT2Product } from '../types/pmt2Types';

// Import functions from the new modules
import { parseNumericValue, formatDateMMDDYYYY } from './pmt2Utils';
import {
  checkSpreadsheetAccess,
  getSpreadsheetConfigs,
  addSpreadsheetConfig,
  removeSpreadsheetConfig,
  updateSpreadsheetConfig
} from './pmt2ConfigService';
import { getSyncStatus, triggerManualSync } from './pmt2SyncService';
import {
  getCompanies,
  getProductsByCompany,
  getProductByAsin,
  findProductByAsin
} from './pmt2ProductDataService';
import {
  getASINReviewHistory,
  getPPCSituationHistory,
  getPPCStatusHistory,
  getPriceHistory,
  getASINMarginHistory,
  getClientNoteForAsin // Import new function
} from './pmt2LedgerService';
import {
  getClientNotesGlobal,
  // getClientNotesAsin, // Remove incorrect import
  getFullMarginHistory
} from './pmt2SkuViewerService';
import { fetchSheetData } from './pmt2SheetsService';

// Import dashboard-specific services and types that were already separate
import {
  LatestGpoResultsData,
  getLatestGpoResultsData
} from './pmt2LatestGpoService';
import {
  GpoPerformanceRow,
  getGpoPerformanceData
} from './pmt2GpoPerformanceService';
import {
  GpoHistoryEntry,
  getGpoHistory
} from './pmt2GpoHistoryService';

import {
  FarmingBidHistoryEntry,
  FarmingBidHistoryData,
  getFarmingBidHistoryData
} from './pmt2FarmingBidHistoryService';
import {
  ExperimentResultsData,
  getExperimentResultsData
} from './pmt2ExperimentResultsService';
import {
  SerpRanksData,
  SerpRankEntry,
  getSerpRanksData
} from './pmt2SerpRanksService';
import {
  TargetCountData,
  TargetCountEntry,
  getTargetCountData,
  getAllTargetCountData
} from './pmt2TargetCountService';
import {
  SevenDayCampaignData,
  SevenDayCampaignResult,
  getSevenDayCampaignResultsData,
  getAllSevenDayCampaignResultsData,
  getSevenDayCampaignResultsByCampaignType,
  getAggregatedSevenDayCampaignResults
} from './pmt2SevenDayCampaignResultsService';
// Re-export types needed by consumers
import { FarmingResultsData, getFarmingResultsData } from './pmt3FarmingResultsService';

export type {
    PMT2SpreadsheetConfig,
    PMT2Product,
    ExperimentResultsData,
    LatestGpoResultsData,
    GpoPerformanceRow,
    GpoHistoryEntry, // Added comma here
    FarmingBidHistoryEntry,
    FarmingBidHistoryData,
    SerpRanksData,
    SerpRankEntry,
    FarmingResultsData,
    TargetCountData,
    TargetCountEntry,
    SevenDayCampaignData,
    SevenDayCampaignResult,
};

// Define the type for the pmt2Service object
interface PMT2Service {
  // Spreadsheet access
  checkSpreadsheetAccess: (spreadsheetId: string) => Promise<{ accessible: boolean; error?: string; details?: any }>;

  // Spreadsheet Configuration Management
  getSpreadsheetConfigs: () => Promise<PMT2SpreadsheetConfig[]>;
  addSpreadsheetConfig: (spreadsheetId: string, companyName: string) => Promise<string>;
  removeSpreadsheetConfig: (configId: string) => Promise<void>;
  updateSpreadsheetConfig: (configId: string, newCompanyName: string) => Promise<void>;

  // Sync Management
  getSyncStatus: () => Promise<any | null>;
  triggerManualSync: () => Promise<void>;

  // Product Data Access
  getCompanies: () => Promise<string[]>;
  getProductsByCompany: (companyName: string) => Promise<PMT2Product[]>;
  getProductByAsin: (companyName: string, asin: string) => Promise<PMT2Product | null>;
  findProductByAsin: (asin: string) => Promise<{ company: string; product: PMT2Product } | null>;

  // Ledger Data Access
  getASINReviewHistory: (companyName: string, asin: string) => Promise<any[]>;
  getPPCSituationHistory: (companyName: string, asin: string) => Promise<any[]>;
  getPPCStatusHistory: (companyName: string, asin: string) => Promise<any[]>;
  getPriceHistory: (companyName: string, asin: string) => Promise<any[]>;
  getASINMarginHistory: (companyName: string, asin: string) => Promise<any[]>;
  getClientNoteForAsin: (companyName: string, asin: string) => Promise<any | null>; // Added new signature

  // SKU Viewer Data Access
  getClientNotesGlobal: (companyName: string) => Promise<any[]>;
  // getClientNotesAsin: (companyName: string) => Promise<any[]>; // Removed incorrect signature
  getFullMarginHistory: (companyName: string) => Promise<any[]>;

  getExperimentResultsData: (companyName: string, asin: string) => Promise<ExperimentResultsData | null>;
  // Helper Functions (Now imported)
  parseNumericValue: (value: any) => number | null;
  formatDateString: (date: Date | null) => string; // Adjusted type to accept null

  // Dashboard Data Access (Already imported)
  getLatestGpoResultsData: (companyName: string, asin: string) => Promise<LatestGpoResultsData | null>;
  getGpoHistory: (companyName: string, asin: string) => Promise<GpoHistoryEntry[]>;
  getSerpRanksData: (companyName: string, asin: string) => Promise<SerpRanksData>;
  getGpoPerformanceData: (companyName: string, asin: string) => Promise<GpoPerformanceRow[]>;

  getFarmingBidHistoryData: (companyName: string, asin: string) => Promise<FarmingBidHistoryData>;
  
  // Target Count Data Access
  getTargetCountData: (companyName: string, asin: string) => Promise<TargetCountData>;
  getAllTargetCountData: (companyName: string) => Promise<TargetCountData>;
  
  // 7 Day Campaign Results Data Access
  getSevenDayCampaignResultsData: (companyName: string, asin: string) => Promise<SevenDayCampaignData | null>;
  getAllSevenDayCampaignResultsData: (companyName: string) => Promise<SevenDayCampaignData[]>;
  getSevenDayCampaignResultsByCampaignType: (companyName: string, asin: string, campaignTypes: string[]) => Promise<SevenDayCampaignResult[]>;
  getAggregatedSevenDayCampaignResults: (companyName: string, asin: string) => Promise<SevenDayCampaignResult | null>;
  
  // Direct Google Sheets Access
  fetchSheetData: (spreadsheetId: string, sheetName: string, rangeA1: string) => Promise<any[][]>;
  getFarmingResultsData: (companyName: string, asin: string) => Promise<FarmingResultsData | null>;
}

// Export the service object, mapping properties to imported functions
export const pmt2Service: PMT2Service = {
  // Spreadsheet access
  checkSpreadsheetAccess,

  // Spreadsheet Configuration Management
  getSpreadsheetConfigs,
  addSpreadsheetConfig,
  removeSpreadsheetConfig,
  updateSpreadsheetConfig,

  // Sync Management
  getSyncStatus,
  triggerManualSync,

  // Product Data Access
  getCompanies,
  getProductsByCompany,
  getProductByAsin,
  findProductByAsin,

  // Ledger Data Access
  getASINReviewHistory,
  getPPCSituationHistory,
  getPPCStatusHistory,
  getPriceHistory,
  getASINMarginHistory,
  getClientNoteForAsin, // Added new function

  // SKU Viewer Data Access
  getClientNotesGlobal,
  // getClientNotesAsin, // Removed incorrect property
  getExperimentResultsData,
  getFullMarginHistory,

  // Helper Functions
  parseNumericValue,
  formatDateString: formatDateMMDDYYYY, // Map to the imported function

  // Dashboard Data Access
  getLatestGpoResultsData,
  getSerpRanksData,
  getGpoHistory,
  getGpoPerformanceData,

  getFarmingBidHistoryData,

  getFarmingResultsData,

  // Target Count Data Access
  getTargetCountData,
  getAllTargetCountData,
  
  // 7 Day Campaign Results Data Access
  getSevenDayCampaignResultsData,
  getAllSevenDayCampaignResultsData,
  getSevenDayCampaignResultsByCampaignType,
  getAggregatedSevenDayCampaignResults,

  // Direct Google Sheets Access
  fetchSheetData,
};
